import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FacilityInventoryProduct } from '../../../../model/FacilityInventoryProduct';
import { ManufacturingSet } from '../../../../model/ManufacturingSet';
import { Product } from '../../../../model/Product';
//

@Component({
  selector: 'app-replenishment-selector',
  templateUrl: './replenishment-selector.component.html',
  styleUrls: ['./replenishment-selector.component.css']
})
export class ReplenishmentSelectorComponent {
  @Input() manufacturingSet: ManufacturingSet;
  @Input() facilityInventoryProducts: FacilityInventoryProduct[];
  filteredProducts: any[] = [];
  utilizationBefore: number = 0;
  selectedOption: any = null;
  selectedQuantity: number = 1;
  /** When true (default), options whose books include a trim size that
   *  doesn't match the manufacturing set's trim size are hidden. The user
   *  can toggle this off to see every eligible option (mismatches still
   *  render in red). */
  hideTrimMismatches: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<ReplenishmentSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.manufacturingSet = data.manufacturingSet;
    this.facilityInventoryProducts = data.facilityInventoryProducts;
    // Only allow replenishment for SINGLES sets
    if (this.manufacturingSet?.details?.SinglesOrBatch !== 'SINGLES') {
      // Optionally show a message here, but for now just close
      this.dialogRef.close();
      return;
    }
    this.filterProducts();
  }

  // Helper to compute sheets and utilization for a set of books
  computeSheetsAndUtilization(bookPages: number[], numberUp: number): { totalSheets: number, utilization: number } {
    // Sort books by pages ascending
    const sorted = [...bookPages].sort((a, b) => a - b);
    let totalSheets = 0;
    let totalPages = 0;
    for (let i = 0; i < sorted.length; i += numberUp) {
      const layer = sorted.slice(i, i + numberUp);
      const maxPages = Math.max(...layer);
      totalSheets += maxPages;
      totalPages += layer.reduce((sum, p) => sum + p, 0);
    }
    // Each sheet is 2 pages (front+back)
    const sheets = Math.ceil(totalSheets / 2);
    // Utilization: sum of all pages / (number of books * max pages per layer)
    const utilization = totalPages / (totalSheets * numberUp);
    return { totalSheets: sheets, utilization };
  }

  // Helper to generate all combinations of k elements from arr
  k_combinations(arr: any[], k: number): any[][] {
    if (k === 1) { return arr.map(x => [x]); }
    const combos: any[][] = [];
    for (let i = 0; i < arr.length; i++) {
      const head = arr[i];
      const tailCombos = this.k_combinations(arr.slice(i + 1), k - 1);
      for (const tail of tailCombos) {
        combos.push([head, ...tail]);
      }
    }
    return combos;
  }

  // Helper to generate all possible assignments of fillQty to n titles (1 or 2 copies each, sum = fillQty)
  assignments(n: number, fillQty: number): number[][] {
    const results: number[][] = [];
    const helper = (arr: number[], idx: number, left: number) => {
      if (idx === n) {
        if (left === 0) { results.push([...arr]); }
        return;
      }
      for (let i = 1; i <= Math.min(2, left); i++) {
        arr[idx] = i;
        helper(arr, idx + 1, left - i);
      }
    };
    helper(new Array(n).fill(0), 0, fillQty);
    return results;
  }

  /**
   * Determines if a book is a 2UP or 4UP book based on trim size.
   * Returns '2UP' if height/72 > heightBreak OR width/72 > widthBreak, else '4UP'.
   * If set.details.numberUpHeightBreak or numberUpWidthBreak are present, use them; otherwise, use 9 and 6.
   */
  getBookUpType(book: any, set: ManufacturingSet): '2UP' | '4UP' {
    if (!book || !book.body) { return '4UP'; }
    const heightBreak = set.details?.numberUpHeightBreak ?? 9;
    const widthBreak = set.details?.numberUpWidthBreak ?? 6;
    const heightIn = (book.body.height || 0) / 72;
    const widthIn = (book.body.width || 0) / 72;
    if (heightIn > heightBreak || widthIn > widthBreak) {
      return '2UP';
    }
    return '4UP';
  }

  /**
   * Converts width/height in points to a trim size string rounded to nearest 0.25 inch, formatted as "6.00x9.00"
   */
  getTrimSizeString(book: any): string {
    if (!book || !book.body) { return ''; }
    const roundQtr = (val: number) => Math.round(val * 4) / 4;
    const width = roundQtr((book.body.width || 0) / 72);
    const height = roundQtr((book.body.height || 0) / 72);
    return `${width.toFixed(2)}x${height.toFixed(2)}`;
  }

  // Helper to get the set trim size string (from details.trimSize if present, else from first book)
  getSetTrimSizeString(): string {
    const details = this.manufacturingSet?.details;
    if (details?.trimSize) { return details.trimSize; }
    const firstBook = this.manufacturingSet?.printNodeOrderItems?.[0]?.book;
    return this.getTrimSizeString(firstBook);
  }

  // Helper to check if any book in option differs in trim size from set, or if set trim size is 'Non Standard'
  optionHasDifferentTrimSize(option: any, setTrimSize: string): boolean {
    if (!option.facilityInventoryProducts) { return false; }
    if (setTrimSize === 'Non Standard') { return true; }
    return option.facilityInventoryProducts.some((prod: any) => this.getTrimSizeString(prod.product.book) !== setTrimSize);
  }

  filterProducts() {
    const set = this.manufacturingSet;
    const numberUp = set.details?.numberUp || 1;
    const setPrintNodeId = set.printnode_id;
    // Get current book page counts (repeat for quantity)
    const currentBookPages: number[] = [];
    if (set.printNodeOrderItems) {
      set.printNodeOrderItems.forEach(item => {
        const qty = item.quantity || 1;
        const pages = item.book?.body?.numPages || 0;
        for (let i = 0; i < qty; i++) {
          currentBookPages.push(pages);
        }
      });
    }
    // Compute current sheets and utilization
    const current = this.computeSheetsAndUtilization(currentBookPages, numberUp);
    this.utilizationBefore = Math.round(current.utilization * 1000) / 10; // percent
    // Determine the up type of the current set (2UP or 4UP)
    let setUpType: '2UP' | '4UP' = '4UP';
    if (set.printNodeOrderItems && set.printNodeOrderItems.length > 0) {
      // Use the first book in the set as representative
      setUpType = this.getBookUpType(set.printNodeOrderItems[0].book, set);
    }
    // Body paperType the set is being printed on. Sets are grouped by
    // paperType upstream (see SetPlanningGroup), so the first item's
    // book.body.paperType_ID is representative. Prefer the explicit
    // details.paperType_ID when present.
    const setBodyPaperTypeId: number | undefined =
      set.details?.paperType_ID ??
      set.printNodeOrderItems?.[0]?.book?.body?.paperType_ID;
    const eligible = (this.facilityInventoryProducts || [])
      .filter(fip => {
        if (fip.inventoryManagementType !== 'LEVEL') { return false; }
        if (fip.printnode_ID !== setPrintNodeId) { return false; }
        const qoh = fip.quantityOnHand || 0;
        const qoo = fip.quantityOnOrder || 0;
        const qc = fip.quantityCommited || 0;
        if (qoh === 0 && qoo === 0 && qc === 0) { return false; }
        if (!fip.product || !fip.product.book) { return false; }
        // Never offer an archived Product as a replenishment option.
        // An archived Product is retained only so historical usage
        // records keep resolving - it must not be ordered again. Note
        // this is deliberately a check on the FacilityInventoryProduct's
        // CURRENT product_ID (fip.product); the archived ids parked in
        // fip.productIds are history and are not consulted here.
        if (Product.isArchived(fip.product)) { return false; }
        if (set.details?.bindingType && fip.product.book.bindingType !== set.details.bindingType) { return false; }
        if (set.details?.laminateType && fip.product.book.cover?.laminateType !== set.details.laminateType) { return false; }
        if (set.details?.interiorType && fip.product.book.interiorType !== set.details.interiorType) { return false; }
        // Body paperType MUST match the set's body paperType. The press
        // is loaded with one paper stock per run, so mixing paperTypes
        // is never valid for replenishment.
        if (setBodyPaperTypeId != null &&
            fip.product.book.body?.paperType_ID !== setBodyPaperTypeId) {
          return false;
        }
        // Only allow books with matching up type
        const fipUpType = this.getBookUpType(fip.product.book, set);
        return fipUpType === setUpType;
      });
    const numBooks = set.printNodeOrderItems?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
    const remainder = numBooks % numberUp;
    // If set is full, allow adding a full new layer
    const fillQty = remainder === 0 ? numberUp : numberUp - remainder;
    const options: any[] = [];
    if (numberUp > 1 && fillQty > 1 && eligible.length > 1) {
      // Try all 2-title and 3-title combinations
      for (let k = 2; k <= Math.min(3, eligible.length, fillQty); k++) {
        const combos = this.k_combinations(eligible, k);
        for (const combo of combos) {
          // For each combo, try all assignments of fillQty to k titles (1 or 2 copies each)
          for (const qtys of this.assignments(k, fillQty)) {
            // Check inventory for each
            let ok = true;
            for (let i = 0; i < k; i++) {
              const fip = combo[i];
              const netInventory = (fip.quantityOnHand || 0) + (fip.quantityOnOrder || 0) - (fip.quantityCommited || 0);
              if (qtys[i] > netInventory) { ok = false; break; }
            }
            if (!ok) { continue; }
            // Build newBookPages
            const newBookPages = [...currentBookPages];
            for (let i = 0; i < k; i++) {
              const numPages = combo[i].product.book.body?.numPages || combo[i].book?.body?.numPages || 0;
              for (let j = 0; j < qtys[i]; j++) {
                newBookPages.push(numPages);
              }
            }
            const after = this.computeSheetsAndUtilization(newBookPages, numberUp);
            const sheetsAdded = Math.max(0, after.totalSheets - current.totalSheets); // Clamp to 0 to avoid negative due to rounding
            const utilizationAfter = Math.round(after.utilization * 1000) / 10; // percent
            const utilizationDelta = Math.round((utilizationAfter - this.utilizationBefore) * 10) / 10;
            // For multi-title options:
            options.push({
              facilityInventoryProducts: combo,
              quantities: qtys,
              titles: combo.map((c, idx) => c.product.book.title + (qtys[idx] > 1 ? ` (x${qtys[idx]})` : '')),
              book_IDs: combo.map(c => c.product.book.ID),
              netInventories: combo.map(fip => (fip.quantityOnHand || 0) + (fip.quantityOnOrder || 0) - (fip.quantityCommited || 0)),
              sheetsAdded,
              numPages: combo.map(c => c.product.book.body?.numPages || 0),
              fillQty,
              utilizationAfter,
              utilizationDelta,
              inventoryScores: combo.map(c => c.product.book.inventoryScore ?? null),
              trimSizes: combo.map(c => this.getTrimSizeString(c.product.book))
            });
          }
        }
      }
    }
    // Also include single-title options (for all fillQty)
    for (const fip of eligible) {
      const netInventory = (fip.quantityOnHand || 0) + (fip.quantityOnOrder || 0) - (fip.quantityCommited || 0);
      if (netInventory < fillQty) { continue; }
      const numPages = fip.product.book.body?.numPages || 0;
      const newBookPages = [...currentBookPages];
      for (let i = 0; i < fillQty; i++) {
        newBookPages.push(numPages);
      }
      const after = this.computeSheetsAndUtilization(newBookPages, numberUp);
      const sheetsAdded = Math.max(0, after.totalSheets - current.totalSheets); // Clamp to 0 to avoid negative due to rounding
      const utilizationAfter = Math.round(after.utilization * 1000) / 10; // percent
      const utilizationDelta = Math.round((utilizationAfter - this.utilizationBefore) * 10) / 10;
      // For single-title options:
      options.push({
        facilityInventoryProducts: [fip],
        quantities: [fillQty],
        titles: [fip.product.book.title + (fillQty > 1 ? ` (x${fillQty})` : '')],
        book_IDs: [fip.product.book.ID],
        netInventories: [netInventory],
        sheetsAdded,
        numPages: [numPages],
        fillQty,
        utilizationAfter,
        utilizationDelta,
        inventoryScores: [fip.product.book.inventoryScore ?? null],
        trimSizes: [this.getTrimSizeString(fip.product.book)]
      });
    }
    // Sort by sheetsAdded, then utilizationDelta desc
    this.filteredProducts = options
      .filter(opt => opt.fillQty > 0)
      .sort((a, b) => a.sheetsAdded - b.sheetsAdded || b.utilizationDelta - a.utilizationDelta);
  }

  selectProduct(option: any) {
    this.selectedOption = option;
  }

  /** Apply the trim-mismatch hide toggle to the sorted option list. */
  visibleProducts(): any[] {
    if (!this.hideTrimMismatches) { return this.filteredProducts; }
    const setTrimSize = this.getSetTrimSizeString();
    return this.filteredProducts.filter(opt => !this.optionHasDifferentTrimSize(opt, setTrimSize));
  }

  /** True when at least one option is being hidden by the toggle. */
  get hiddenCount(): number {
    if (!this.hideTrimMismatches) { return 0; }
    const setTrimSize = this.getSetTrimSizeString();
    return this.filteredProducts.filter(opt => this.optionHasDifferentTrimSize(opt, setTrimSize)).length;
  }

  /** Clear the selection if the hidden state filters it out. */
  setHideTrimMismatches(hide: boolean): void {
    this.hideTrimMismatches = hide;
    if (this.selectedOption && hide) {
      const setTrimSize = this.getSetTrimSizeString();
      if (this.optionHasDifferentTrimSize(this.selectedOption, setTrimSize)) {
        this.selectedOption = null;
      }
    }
  }

  confirm() {
    if (this.selectedOption) {
      this.dialogRef.close({
        facilityInventoryProducts: this.selectedOption.facilityInventoryProducts,
        quantities: this.selectedOption.quantities,
        titles: this.selectedOption.titles,
        book_IDs: this.selectedOption.book_IDs,
        netInventories: this.selectedOption.netInventories,
        sheetsAdded: this.selectedOption.sheetsAdded,
        numPages: this.selectedOption.numPages,
        fillQty: this.selectedOption.fillQty,
        utilizationAfter: this.selectedOption.utilizationAfter,
        utilizationDelta: this.selectedOption.utilizationDelta,
        inventoryScores: this.selectedOption.inventoryScores,
        selectedQuantity: this.selectedQuantity
      });
    }
  }

  cancel() {
    this.dialogRef.close();
  }
}
